<div class="space-y-3">

  <!-- ── Note ID input bar ─────────────────────────────────────────────────── -->
  <div class="rounded-xl border border-slate-200 bg-slate-50 p-3">
    <div class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto_auto] md:items-end">
      <label class="block">
        <span class="text-xs text-slate-600">Note ID</span>
        <input
          pInputText
          type="text"
          [(ngModel)]="noteId"
          placeholder="Paste note ID (note:…)"
          class="mt-1 w-full"
          (keyup.enter)="inspectNote()" />
      </label>
      <button
        pButton
        pRipple
        type="button"
        label="Inspect"
        icon="pi pi-search"
        [loading]="noteLoading"
        [disabled]="!noteId.trim()"
        (click)="inspectNote()"></button>
      <button
        pButton
        pRipple
        type="button"
        label="Create Embeddings"
        icon="pi pi-plus"
        severity="success"
        [loading]="createEmbeddingsLoading"
        [disabled]="!noteId.trim() || deleteEmbeddingsLoading"
        (click)="createEmbeddings()"></button>
      <button
        pButton
        pRipple
        type="button"
        label="Delete Embeddings"
        icon="pi pi-trash"
        severity="danger"
        [loading]="deleteEmbeddingsLoading"
        [disabled]="!noteId.trim() || createEmbeddingsLoading || !endpointAvailability.canDeleteEmbeddingsForNote"
        [title]="endpointAvailability.canDeleteEmbeddingsForNote ? 'Delete embeddings for this note' : 'No delete endpoint available'"
        (click)="deleteEmbeddings()"></button>
    </div>

    <div *ngIf="embeddingsMessage" class="mt-2 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">
      {{ embeddingsMessage }}
    </div>
    <div *ngIf="noteError" class="mt-2 text-sm text-rose-600">{{ noteError }}</div>
  </div>

  <!-- ── Tab bar ───────────────────────────────────────────────────────────── -->
  <div class="flex flex-wrap gap-1.5">
    <button
      *ngFor="let tab of [
        { id: 'overview', label: 'Overview', icon: 'pi-chart-bar' },
        { id: 'chunks',   label: 'Indexed Chunks', icon: 'pi-list' },
        { id: 'retrieval',label: 'Retrieval Test', icon: 'pi-bolt' },
        { id: 'semantic', label: 'Semantic Query', icon: 'pi-search' },
        { id: 'raw',      label: 'Raw JSON', icon: 'pi-code' }
      ]"
      type="button"
      class="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition"
      [ngClass]="activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'"
      (click)="setTab($any(tab.id))">
      <i class="pi" [ngClass]="tab.icon"></i>
      {{ tab.label }}
    </button>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <!-- Tab: Overview                                                           -->
  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="activeTab === 'overview'" class="space-y-4">

    <!-- Loading skeleton -->
    <div *ngIf="noteLoading" class="space-y-3">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div *ngFor="let _ of [1,2,3,4,5,6]" class="h-16 animate-pulse rounded-lg bg-slate-100"></div>
      </div>
    </div>

    <!-- No data yet -->
    <div *ngIf="!noteLoading && !overview" class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
      <i class="pi pi-search mb-2 block text-2xl"></i>
      Enter a note ID above and click Inspect to view index details.
    </div>

    <!-- Overview cards + warnings -->
    <ng-container *ngIf="!noteLoading && overview">

      <!-- Status banner -->
      <div class="flex flex-wrap items-center gap-3">
        <p-tag [value]="overviewSummaryBadge().label" [severity]="overviewSummaryBadge().severity" />
        <span class="text-sm text-slate-600">{{ overview.chunkCount }} chunk(s) indexed</span>
      </div>

      <!-- Summary cards grid -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Note Title</div>
          <div class="truncate text-sm font-semibold text-slate-800" [title]="overview.noteTitle">
            {{ overview.noteTitle || '—' }}
          </div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Note ID</div>
          <div class="flex items-center gap-1">
            <span class="truncate font-mono text-xs text-slate-700" [title]="overview.noteId">{{ overview.noteId }}</span>
            <button pButton pRipple type="button"
              [icon]="recentlyCopied.has('overview-note-id') ? 'pi pi-check' : 'pi pi-copy'"
              [text]="true" size="small"
              (click)="copyToClipboard(overview.noteId, 'overview-note-id')"></button>
          </div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Chunks / Total Tokens</div>
          <div class="text-sm font-semibold text-slate-800">
            {{ overview.chunkCount }} chunks &bull; {{ overview.totalTokens }} tokens
          </div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Embedding Model</div>
          <div class="font-mono text-xs text-slate-700">{{ overview.embeddingModel || '—' }}</div>
          <div *ngIf="overview.embeddingDimension" class="text-xs text-slate-500">{{ overview.embeddingDimension }} dim</div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Schema / Strategy</div>
          <div class="font-mono text-xs text-slate-700">
            <span *ngIf="overview.schemaVersion">v{{ overview.schemaVersion }}</span>
            <span *ngIf="overview.strategyVersion"> / {{ overview.strategyVersion }}</span>
            <span *ngIf="!overview.schemaVersion && !overview.strategyVersion">—</span>
          </div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Last Indexed</div>
          <div class="text-xs text-slate-700">{{ (overview.lastIndexedAt | date:'medium') || '—' }}</div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Note Last Modified</div>
          <div class="text-xs text-slate-700">{{ (overview.noteLastModified | date:'medium') || '—' }}</div>
        </div>

        <div class="rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Reference Date</div>
          <div class="text-xs text-slate-700">{{ (overview.noteReferenceDate | date:'mediumDate') || '—' }}</div>
        </div>
      </div>

      <!-- Warnings -->
      <div class="space-y-2">
        <div class="text-xs font-medium uppercase tracking-wide text-slate-500">Index Diagnostics</div>
        <div
          *ngFor="let w of warnings"
          class="flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
          [ngClass]="warningBgClass(w.severity)">
          <i class="pi mt-0.5 shrink-0 text-base" [ngClass]="warningIcon(w.severity)"></i>
          <span>{{ w.message }}</span>
        </div>
      </div>

    </ng-container>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <!-- Tab: Indexed Chunks                                                      -->
  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="activeTab === 'chunks'" class="space-y-3">

    <div *ngIf="noteLoading" class="py-8 text-center text-sm text-slate-500">
      <i class="pi pi-spin pi-spinner mb-2 block text-2xl"></i>
      Loading chunks…
    </div>

    <div *ngIf="!noteLoading && chunks.length === 0 && !noteError"
      class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
      <i class="pi pi-list mb-2 block text-2xl"></i>
      No chunks loaded. Enter a note ID and click Inspect.
    </div>

    <ng-container *ngIf="!noteLoading && chunks.length > 0">

      <!-- Term filter -->
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span
          class="text-xs text-slate-500 cursor-help"
          pTooltip="Filters the normalized search terms shown inside each expanded chunk row. Terms are the keywords extracted and stored in the index for full-text and semantic retrieval."
          tooltipPosition="top">Term filter:</span>
        <button *ngFor="let f of [
            { id: 'all',        label: 'All',        tooltip: 'Show every normalized term extracted from this chunk' },
            { id: 'no-short',   label: 'No short',   tooltip: 'Hide terms shorter than 4 characters (e.g. articles, prepositions, single letters)' },
            { id: 'no-numbers', label: 'No numbers', tooltip: 'Hide terms that consist entirely of digits' },
            { id: 'no-urls',    label: 'No URLs',    tooltip: 'Hide terms that begin with http or https' },
            { id: 'dates-only', label: 'Dates only', tooltip: 'Show only terms that contain a 4-digit year — useful for auditing date coverage in the index' }
          ]"
          type="button"
          class="rounded px-2 py-0.5 text-xs"
          [ngClass]="termFilter === f.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
          [pTooltip]="f.tooltip"
          tooltipPosition="top"
          (click)="termFilter = $any(f.id)">{{ f.label }}</button>
      </div>

      <!-- Chunks table -->
      <div class="overflow-x-auto rounded-lg border border-slate-200">
        <p-table
          [value]="chunks"
          dataKey="id"
          styleClass="p-datatable-sm p-datatable-gridlines"
          [resizableColumns]="true"
          columnResizeMode="expand"
          [tableStyle]="{ 'min-width': '48rem' }">

          <ng-template pTemplate="header">
            <tr class="text-xs uppercase tracking-wide text-slate-500">
              <th style="width: 2.5rem"></th>
              <th pResizableColumn style="width: 3rem">#</th>
              <th pResizableColumn>Heading</th>
              <th pResizableColumn style="width: 6rem">Tokens</th>
              <th pResizableColumn style="width: 6rem">Length</th>
              <th pResizableColumn>Status</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-chunk let-expanded="expanded">
            <tr class="hover:bg-slate-50">
              <td>
                <button
                  type="button"
                  pButton
                  pRipple
                  [pRowToggler]="chunk"
                  [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                  [text]="true"
                  severity="secondary"
                  size="small"></button>
              </td>
              <td class="font-mono text-xs text-slate-500">{{ chunk.chunkIndex }}</td>
              <td>
                <span class="block truncate text-sm" [title]="chunk.headingPath">
                  {{ chunk.headingPath || '—' }}
                </span>
              </td>
              <td class="font-mono text-sm">{{ chunk.tokenCount ?? '—' }}</td>
              <td class="font-mono text-sm">{{ chunk.textLength ?? '—' }}</td>
              <td>
                <div class="flex flex-wrap gap-1">
                  <p-tag
                    *ngFor="let b of chunk.statusBadges"
                    [value]="b.label"
                    [severity]="b.severity" />
                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="expandedrow" let-chunk>
            <tr>
              <td colspan="6" class="p-0">
                <div class="space-y-4 bg-slate-50 p-4">

                  <!-- (A) Content -->
                  <div>
                    <div class="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>Content</span>
                      <button pButton pRipple type="button"
                        [icon]="recentlyCopied.has('chunk-text-' + chunk.id) ? 'pi pi-check' : 'pi pi-copy'"
                        [text]="true" size="small"
                        pTooltip="Copy chunk text"
                        (click)="copyToClipboard(chunk.fullText, 'chunk-text-' + chunk.id)"></button>
                    </div>
                    <pre class="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-3 text-xs text-slate-800">{{ chunk.fullText }}</pre>
                  </div>

                  <!-- (B) Identity + (C) Metadata side-by-side on wide -->
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">

                    <!-- (B) Identity -->
                    <div>
                      <div class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Identity</div>
                      <dl class="space-y-1 text-xs">
                        <div class="flex gap-2">
                          <dt class="w-28 shrink-0 text-slate-500">Chunk ID</dt>
                          <dd class="flex min-w-0 items-center gap-1 font-mono">
                            <span class="truncate text-slate-700" [title]="chunk.id">{{ chunk.id || '—' }}</span>
                            <button *ngIf="chunk.id" pButton pRipple type="button"
                              [icon]="recentlyCopied.has('chunk-id-' + chunk.id) ? 'pi pi-check' : 'pi pi-copy'"
                              [text]="true" size="small"
                              (click)="copyToClipboard(chunk.id, 'chunk-id-' + chunk.id)"></button>
                          </dd>
                        </div>
                        <div class="flex gap-2">
                          <dt class="w-28 shrink-0 text-slate-500">Chunk Index</dt>
                          <dd class="font-mono text-slate-700">{{ chunk.chunkIndex }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.chunkIndexInSection !== null">
                          <dt class="w-28 shrink-0 text-slate-500">In-Section #</dt>
                          <dd class="font-mono text-slate-700">{{ chunk.chunkIndexInSection }}</dd>
                        </div>
                        <div class="flex gap-2">
                          <dt class="w-28 shrink-0 text-slate-500">Section ID</dt>
                          <dd class="truncate font-mono text-slate-700">{{ chunk.sectionId || '—' }}</dd>
                        </div>
                        <div class="flex gap-2">
                          <dt class="w-28 shrink-0 text-slate-500">Content Hash</dt>
                          <dd class="truncate font-mono text-slate-700">{{ chunk.contentHash || '—' }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.chunkType">
                          <dt class="w-28 shrink-0 text-slate-500">Chunk Type</dt>
                          <dd class="text-slate-700">{{ chunk.chunkType }}</dd>
                        </div>
                      </dl>
                    </div>

                    <!-- (C) Metadata -->
                    <div>
                      <div class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Note Metadata</div>
                      <dl class="space-y-1 text-xs">
                        <div class="flex gap-2">
                          <dt class="w-28 shrink-0 text-slate-500">Note Title</dt>
                          <dd class="text-slate-700">{{ chunk.noteTitle || '—' }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteStatus">
                          <dt class="w-28 shrink-0 text-slate-500">Note Status</dt>
                          <dd class="text-slate-700">{{ chunk.noteStatus }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteLastModified">
                          <dt class="w-28 shrink-0 text-slate-500">Last Modified</dt>
                          <dd class="text-slate-700">{{ chunk.noteLastModified | date:'medium' }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteCreated">
                          <dt class="w-28 shrink-0 text-slate-500">Created</dt>
                          <dd class="text-slate-700">{{ chunk.noteCreated | date:'medium' }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteReferenceDate">
                          <dt class="w-28 shrink-0 text-slate-500">Reference Date</dt>
                          <dd class="text-slate-700">{{ chunk.noteReferenceDate | date:'mediumDate' }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteKeywords">
                          <dt class="w-28 shrink-0 text-slate-500">Keywords</dt>
                          <dd class="text-slate-700">{{ chunk.noteKeywords }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteAiKeywords">
                          <dt class="w-28 shrink-0 text-slate-500">AI Keywords</dt>
                          <dd class="text-slate-700">{{ chunk.noteAiKeywords }}</dd>
                        </div>
                        <div class="flex gap-2" *ngIf="chunk.noteTags.length > 0">
                          <dt class="w-28 shrink-0 text-slate-500">Tags</dt>
                          <dd class="text-slate-700">{{ chunk.noteTags.join(', ') }}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  <!-- (D) Technical -->
                  <div>
                    <div class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Technical</div>
                    <div class="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                      <span><span class="text-slate-500">Embedding model:</span> <span class="font-mono text-slate-700">{{ chunk.embeddingModel || '—' }}</span></span>
                      <span *ngIf="chunk.embeddingDimension !== null"><span class="text-slate-500">Dimensions:</span> <span class="font-mono text-slate-700">{{ chunk.embeddingDimension }}</span></span>
                      <span><span class="text-slate-500">Tokens:</span> <span class="font-mono text-slate-700">{{ chunk.tokenCount ?? '—' }}</span></span>
                      <span><span class="text-slate-500">Text length:</span> <span class="font-mono text-slate-700">{{ chunk.textLength ?? '—' }}</span></span>
                      <span *ngIf="chunk.sourceTextLength !== null"><span class="text-slate-500">Source length:</span> <span class="font-mono text-slate-700">{{ chunk.sourceTextLength }}</span></span>
                      <span *ngIf="chunk.schemaVersion"><span class="text-slate-500">Schema v:</span> <span class="font-mono text-slate-700">{{ chunk.schemaVersion }}</span></span>
                      <span *ngIf="chunk.strategyVersion"><span class="text-slate-500">Strategy v:</span> <span class="font-mono text-slate-700">{{ chunk.strategyVersion }}</span></span>
                      <span *ngIf="chunk.indexedAt"><span class="text-slate-500">Indexed at:</span> <span class="font-mono text-slate-700">{{ chunk.indexedAt | date:'medium' }}</span></span>
                    </div>
                  </div>

                  <!-- (E) Terms as chips -->
                  <div *ngIf="chunk.normalizedTerms.length > 0">
                    <div class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Normalized Terms ({{ getFilteredTerms(chunk).length }}/{{ chunk.normalizedTerms.length }})
                    </div>
                    <div class="flex flex-wrap gap-1">
                      <p-chip
                        *ngFor="let term of getFilteredTerms(chunk)"
                        [label]="term"
                        styleClass="text-xs" />
                    </div>
                    <div *ngIf="getFilteredTerms(chunk).length === 0" class="text-xs text-slate-400">
                      All terms filtered out by current filter.
                    </div>
                  </div>

                  <!-- (F) Raw metadata JSON (collapsible) -->
                  <p-panel header="Raw Metadata JSON" [toggleable]="true" [collapsed]="true">
                    <div class="flex justify-end">
                      <button pButton pRipple type="button"
                        [icon]="recentlyCopied.has('chunk-meta-' + chunk.id) ? 'pi pi-check' : 'pi pi-copy'"
                        [text]="true" size="small" label="Copy"
                        (click)="copyToClipboard(formatJson(chunk.metadata), 'chunk-meta-' + chunk.id)"></button>
                    </div>
                    <pre class="max-h-72 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{{ formatJson(chunk.metadata) }}</pre>
                  </p-panel>

                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="py-8 text-center text-sm text-slate-500">No chunks found.</td>
            </tr>
          </ng-template>

        </p-table>
      </div>
    </ng-container>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <!-- Tab: Retrieval Test                                                      -->
  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="activeTab === 'retrieval'" class="space-y-3">

    <div class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_120px_auto] md:items-end">
      <label class="block">
        <span class="text-xs text-slate-600">Question</span>
        <input
          pInputText
          type="text"
          [(ngModel)]="retrievalQuery"
          placeholder="How do I deploy staging?"
          class="mt-1 w-full"
          (keyup.enter)="runRetrievalTest()" />
      </label>
      <label class="block">
        <span class="text-xs text-slate-600">Top K</span>
        <input
          pInputText
          type="number"
          min="1"
          [(ngModel)]="retrievalK"
          class="mt-1 w-full" />
      </label>
      <button
        pButton
        pRipple
        type="button"
        label="Run Retrieval Test"
        icon="pi pi-bolt"
        [loading]="retrievalLoading"
        [disabled]="!retrievalQuery.trim()"
        (click)="runRetrievalTest()"></button>
    </div>

    <div *ngIf="retrievalError" class="text-sm text-rose-600">{{ retrievalError }}</div>

    <!-- Context note tip -->
    <div *ngIf="noteId.trim() && retrievalResults.length > 0"
      class="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
      <i class="pi pi-info-circle mr-1"></i>
      Chunks from <strong>{{ noteId }}</strong> are highlighted with the "Current Note" badge.
    </div>

    <!-- Results summary -->
    <div *ngIf="!retrievalLoading && retrievalResults.length > 0" class="text-sm text-slate-600">
      {{ retrievalResults.length }} result(s) returned.
    </div>

    <!-- No results -->
    <div *ngIf="!retrievalLoading && !retrievalError && retrievalResults.length === 0 && rawRetrievalData !== null"
      class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
      No results returned.
    </div>

    <!-- Results table -->
    <div *ngIf="retrievalResults.length > 0" class="overflow-x-auto rounded-lg border border-slate-200">
      <p-table
        [value]="retrievalResults"
        dataKey="rank"
        styleClass="p-datatable-sm p-datatable-gridlines"
        [resizableColumns]="true"
        columnResizeMode="expand"
        [tableStyle]="{ 'min-width': '56rem' }">

        <ng-template pTemplate="header">
          <tr class="text-xs uppercase tracking-wide text-slate-500">
            <th style="width: 2.5rem"></th>
            <th pResizableColumn style="width: 3.5rem" class="cursor-help"
                pTooltip="Result position ordered by relevance score — 1 is the best match"
                tooltipPosition="top">Rank</th>
            <th pResizableColumn style="width: 5.5rem" class="cursor-help"
                pTooltip="Combined relevance score (0–1). Higher is more relevant. May incorporate semantic similarity, recency boost, and status boost."
                tooltipPosition="top">Score</th>
            <th pResizableColumn class="cursor-help"
                pTooltip="The note this chunk belongs to — title on top, internal note ID below"
                tooltipPosition="top">Note</th>
            <th pResizableColumn class="cursor-help"
                pTooltip="The document section heading under which this chunk was indexed — reflects the note's content structure"
                tooltipPosition="top">Heading</th>
            <th pResizableColumn style="width: 5.5rem" class="cursor-help"
                pTooltip="Number of tokens in this chunk. Smaller chunks are more precise; very large chunks may reduce retrieval quality."
                tooltipPosition="top">Tokens</th>
            <th pResizableColumn class="cursor-help"
                pTooltip="First ~320 characters of the indexed chunk text. Hover a cell for the full text."
                tooltipPosition="top">Preview</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-result let-expanded="expanded">
          <tr [ngClass]="result.isCurrentNote ? 'bg-sky-50' : 'hover:bg-slate-50'">
            <td>
              <button
                type="button"
                pButton
                pRipple
                [pRowToggler]="result"
                [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                [text]="true"
                severity="secondary"
                size="small"></button>
            </td>
            <td class="font-mono text-sm font-semibold text-slate-700">{{ result.rank }}</td>
            <td class="font-mono text-sm text-slate-700">{{ formatScore(result.score) }}</td>
            <td>
              <div class="text-sm font-medium">{{ result.noteTitle || '—' }}</div>
              <div class="font-mono text-[11px] text-slate-500">{{ result.noteId }}</div>
              <div *ngIf="result.statusBadges.length > 0" class="mt-1 flex flex-wrap gap-1">
                <p-tag *ngFor="let b of result.statusBadges" [value]="b.label" [severity]="b.severity" />
              </div>
            </td>
            <td>
              <span class="block truncate text-xs text-slate-600" [title]="result.headingPath">
                {{ result.headingPath || '—' }}
              </span>
            </td>
            <td class="font-mono text-sm">{{ result.tokenCount ?? '—' }}</td>
            <td>
              <span class="block truncate text-xs text-slate-600" [title]="result.fullText">
                {{ result.preview }}
              </span>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="expandedrow" let-result>
          <tr>
            <td colspan="7" class="p-0">
              <div class="space-y-3 bg-slate-50 p-4">

                <!-- Full text -->
                <div>
                  <div class="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Retrieved Text</span>
                    <button pButton pRipple type="button"
                      [icon]="recentlyCopied.has('ret-text-' + result.rank) ? 'pi pi-check' : 'pi pi-copy'"
                      [text]="true" size="small"
                      (click)="copyToClipboard(result.fullText, 'ret-text-' + result.rank)"></button>
                  </div>
                  <pre class="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-3 text-xs text-slate-800">{{ result.fullText }}</pre>
                </div>

                <!-- Score breakdown -->
                <div class="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span><span class="text-slate-500">Score:</span> <span class="font-mono font-semibold">{{ formatScore(result.score) }}</span></span>
                  <span *ngIf="result.raw['semantic_score'] !== undefined"><span class="text-slate-500">Semantic:</span> <span class="font-mono">{{ result.raw['semantic_score'] }}</span></span>
                  <span *ngIf="result.raw['status_boost'] !== undefined"><span class="text-slate-500">Status boost:</span> <span class="font-mono">{{ result.raw['status_boost'] }}</span></span>
                  <span *ngIf="result.raw['recency_boost'] !== undefined"><span class="text-slate-500">Recency boost:</span> <span class="font-mono">{{ result.raw['recency_boost'] }}</span></span>
                </div>

                <!-- Metadata panel -->
                <p-panel header="Chunk Metadata" [toggleable]="true" [collapsed]="true">
                  <div class="flex justify-end">
                    <button pButton pRipple type="button"
                      [icon]="recentlyCopied.has('ret-meta-' + result.rank) ? 'pi pi-check' : 'pi pi-copy'"
                      [text]="true" size="small" label="Copy"
                      (click)="copyToClipboard(formatJson(result.metadata), 'ret-meta-' + result.rank)"></button>
                  </div>
                  <pre class="max-h-48 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{{ formatJson(result.metadata) }}</pre>
                </p-panel>

              </div>
            </td>
          </tr>
        </ng-template>

      </p-table>
    </div>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <!-- Tab: Semantic Query                                                      -->
  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="activeTab === 'semantic'" class="space-y-3">

    <div class="grid grid-cols-1 gap-2 md:grid-cols-[1fr_120px_auto] md:items-end">
      <label class="block">
        <span class="text-xs text-slate-600">Query</span>
        <input
          pInputText
          type="text"
          [(ngModel)]="semanticQuery"
          placeholder="Search meaning across notes…"
          class="mt-1 w-full"
          (keyup.enter)="runSemanticQuery()" />
      </label>
      <label class="block">
        <span class="text-xs text-slate-600">Top K</span>
        <input
          pInputText
          type="number"
          min="1"
          [(ngModel)]="semanticK"
          class="mt-1 w-full" />
      </label>
      <button
        pButton
        pRipple
        type="button"
        label="Search"
        icon="pi pi-search"
        [loading]="semanticLoading"
        [disabled]="!semanticQuery.trim()"
        (click)="runSemanticQuery()"></button>
    </div>

    <div *ngIf="semanticError" class="text-sm text-rose-600">{{ semanticError }}</div>

    <div *ngIf="!semanticLoading && semanticResults.length > 0" class="text-sm text-slate-600">
      {{ semanticResults.length }} result(s) returned.
    </div>

    <div *ngIf="!semanticLoading && !semanticError && semanticResults.length === 0 && rawSemanticData !== null"
      class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
      No results returned.
    </div>

    <div *ngIf="semanticResults.length > 0" class="space-y-2">
      <div *ngFor="let r of semanticResults"
        class="rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <div class="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div class="font-semibold text-slate-800">{{ r.noteTitle || '—' }}</div>
            <div class="mt-0.5 font-mono text-xs text-slate-500">{{ r.noteId }}</div>
          </div>
          <div class="flex items-center gap-2">
            <span class="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
              Score: {{ formatScore(r.score) }}
            </span>
            <span class="text-xs font-medium text-slate-500">#{{ r.rank }}</span>
          </div>
        </div>
        <div *ngIf="r.snippet" class="mt-2 text-xs text-slate-600 leading-relaxed">
          {{ r.snippet }}
        </div>
      </div>
    </div>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <!-- Tab: Raw JSON                                                            -->
  <!-- ════════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="activeTab === 'raw'" class="space-y-3">

    <p-panel header="Note Debug Raw Response" [toggleable]="true" [collapsed]="true">
      <ng-template pTemplate="icons">
        <button pButton pRipple type="button"
          [icon]="recentlyCopied.has('raw-note') ? 'pi pi-check' : 'pi pi-copy'"
          [text]="true" size="small"
          (click)="copyToClipboard(formatJson(rawNoteData), 'raw-note')"></button>
      </ng-template>
      <div *ngIf="rawNoteData; else noRawData">
        <pre class="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{{ formatJson(rawNoteData) }}</pre>
      </div>
      <ng-template #noRawData>
        <div class="text-sm text-slate-400">No note debug data loaded. Inspect a note first.</div>
      </ng-template>
    </p-panel>

    <p-panel header="Retrieval Test Raw Response" [toggleable]="true" [collapsed]="true">
      <ng-template pTemplate="icons">
        <button pButton pRipple type="button"
          [icon]="recentlyCopied.has('raw-retrieval') ? 'pi pi-check' : 'pi pi-copy'"
          [text]="true" size="small"
          (click)="copyToClipboard(formatJson(rawRetrievalData), 'raw-retrieval')"></button>
      </ng-template>
      <div *ngIf="rawRetrievalData; else noRawRetrieval">
        <pre class="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{{ formatJson(rawRetrievalData) }}</pre>
      </div>
      <ng-template #noRawRetrieval>
        <div class="text-sm text-slate-400">No retrieval test data. Run a retrieval test first.</div>
      </ng-template>
    </p-panel>

    <p-panel header="Semantic Query Raw Response" [toggleable]="true" [collapsed]="true">
      <ng-template pTemplate="icons">
        <button pButton pRipple type="button"
          [icon]="recentlyCopied.has('raw-semantic') ? 'pi pi-check' : 'pi pi-copy'"
          [text]="true" size="small"
          (click)="copyToClipboard(formatJson(rawSemanticData), 'raw-semantic')"></button>
      </ng-template>
      <div *ngIf="rawSemanticData; else noRawSemantic">
        <pre class="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{{ formatJson(rawSemanticData) }}</pre>
      </div>
      <ng-template #noRawSemantic>
        <div class="text-sm text-slate-400">No semantic query data. Run a semantic search first.</div>
      </ng-template>
    </p-panel>

  </div>

</div>
